import { useMemo, useRef, useState } from 'react';
import SEOMeta from '../../components/SEOMeta';
import { useDarkMode } from '../../hooks/useDarkMode';
import PixTab from './PixTab';
import CartaoTab from './CartaoTab';
import BoletoTab from './BoletoTab';
import CouponBox, { type CupomAplicado } from './CouponBox';

const FONT = "'DM Sans', sans-serif";
type Plan = 'musica' | 'video';
type Tab = 'pix' | 'cartao' | 'boleto';

export type SimpleCheckoutInfo = {
  ref_id: string;
  product_version: 'simple-v17';
  status: string;
  nome_homenageado: string;
  relacao: string;
  ocasiao: string;
  estilo: string;
  valor_final: number | null;
  plano: Plan | null;
  preview: { audio_url: string; duracao: number | null } | null;
  valores: { musica: number; video: number };
};

function PreviewPlayer({ url, name }: { url: string; name: string }) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  return (
    <div className="glass" style={{ padding: 24, borderRadius: 24, border: '1.5px solid var(--border-soft)', background: 'var(--surface-white)', boxShadow: '0 18px 54px rgba(38,26,18,.08)' }}>
      <div style={{ fontSize: 12, color: 'var(--text-soft)', textTransform: 'uppercase', letterSpacing: '.16em', fontWeight: 700 }}>Sua prévia</div>
      <h2 style={{ margin: '8px 0 20px', fontFamily: FONT, fontSize: 26, fontWeight: 800, letterSpacing: '-.03em', color: 'var(--heading-ink)' }}>Uma música para {name}</h2>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        <button type="button" aria-label={playing ? 'Pausar prévia' : 'Ouvir prévia'} onClick={() => {
          const audio = audioRef.current;
          if (!audio) return;
          if (playing) { audio.pause(); setPlaying(false); }
          else audio.play().then(() => setPlaying(true));
        }} style={{ width: 58, height: 58, borderRadius: '50%', border: 0, background: 'var(--gold)', color: '#fff', cursor: 'pointer', fontSize: 20, boxShadow: '0 8px 26px rgba(255,145,77,.35)' }}>
          {playing ? 'Ⅱ' : '▶'}
        </button>
        <div style={{ flex: 1 }}>
          <div style={{ height: 8, borderRadius: 99, background: 'var(--border-fade)', overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${progress * 100}%`, background: 'linear-gradient(90deg,var(--gold),var(--gold-light))' }} />
          </div>
          <p style={{ margin: '9px 0 0', fontSize: 12, color: 'var(--text-muted)' }}>Ouça com calma. Se quiser mudar algo, é só pedir uma nova tentativa.</p>
        </div>
      </div>
      <audio ref={audioRef} src={url} preload="metadata" onTimeUpdate={(event) => {
        const audio = event.currentTarget;
        setProgress(audio.duration ? audio.currentTime / audio.duration : 0);
      }} onEnded={() => setPlaying(false)} />
    </div>
  );
}

function PlanCard({ id, selected, onSelect, price }: { id: Plan; selected: boolean; onSelect: () => void; price: number }) {
  const video = id === 'video';
  const benefits = video
    ? ['A mesma música personalizada', 'Vídeo profissional com todas as fotos enviadas', 'Tratamento e enquadramento das imagens', 'Entrega em até 30 minutos']
    : ['Uma música personalizada', 'Áudio MP3 em alta qualidade', 'Entrega em até 30 minutos'];
  return (
    <button type="button" onClick={onSelect} style={{ position: 'relative', textAlign: 'left', padding: 22, borderRadius: 22, cursor: 'pointer', fontFamily: FONT, background: 'var(--surface-white)', border: selected ? '2px solid var(--gold)' : '1.5px solid var(--border-soft)', boxShadow: selected ? '0 16px 42px rgba(255,145,77,.2)' : '0 8px 26px rgba(30,20,10,.05)' }}>
      {video && <span style={{ position: 'absolute', top: -12, right: 18, padding: '5px 11px', borderRadius: 99, background: 'var(--gold)', color: '#fff', fontSize: 10, fontWeight: 800, letterSpacing: '.1em' }}>MAIS ESCOLHIDO</span>}
      <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--heading-ink)', letterSpacing: '-.025em' }}>{video ? 'Música + vídeo com fotos' : 'Música'}</div>
      <div style={{ margin: '8px 0 16px', fontSize: 34, fontWeight: 800, color: 'var(--heading-ink)' }}>R$ {price.toFixed(2).replace('.', ',')}</div>
      <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 9 }}>
        {benefits.map((benefit) => <li key={benefit} style={{ display: 'flex', gap: 9, fontSize: 13.5, lineHeight: 1.35, color: 'var(--text-body)' }}><span style={{ color: 'var(--gold)', fontWeight: 800 }}>✓</span>{benefit}</li>)}
      </ul>
    </button>
  );
}

export default function SimpleCheckoutPage({ info, slug }: { info: SimpleCheckoutInfo; slug: string }) {
  const isDark = useDarkMode();
  const [plan, setPlan] = useState<Plan | null>(info.plano === 'musica' || info.plano === 'video' ? info.plano : null);
  const [tab, setTab] = useState<Tab>('pix');
  const [coupon, setCoupon] = useState<CupomAplicado | null>(null);
  const price = plan ? info.valores[plan] : 0;
  const finalPrice = coupon?.valor_final ?? price;
  const paid = ['pago', 'gerando_musica', 'aguardando_fotos', 'preparando_video', 'video_em_qa', 'entregando', 'entregue'].includes(info.status);
  const feedbackUrl = useMemo(
    () => `https://wa.me/5511926797492?text=${encodeURIComponent(`Quero pedir uma alteração na prévia do pedido ${info.ref_id}.`)}`,
    [info.ref_id],
  );

  if (paid) {
    return <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', padding: 24, fontFamily: FONT, background: 'var(--cream)' }}><div style={{ textAlign: 'center', maxWidth: 480 }}><div style={{ fontSize: 42 }}>✓</div><h1 style={{ fontSize: 30, color: 'var(--heading-ink)' }}>Pagamento confirmado</h1><p style={{ color: 'var(--text-muted)', lineHeight: 1.6 }}>Seu pedido está em produção e será enviado pelo WhatsApp em até 30 minutos.</p></div></div>;
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--cream)', fontFamily: FONT }}>
      <SEOMeta title="Sua música — Inspira Músicas" description="Ouça sua prévia e escolha seu pacote" path={`/ouvir-musica/${slug}`} noIndex />
      <header style={{ maxWidth: 760, margin: '0 auto', padding: '36px 20px 18px', textAlign: 'center' }}>
        <img src={isDark ? '/logo-dark.png' : '/logo-light.png'} alt="Inspira Músicas" style={{ height: 36, marginBottom: 25 }} />
        <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: 14 }}>A música feita para</p>
        <h1 style={{ margin: '4px 0 12px', fontSize: 'clamp(38px,8vw,62px)', fontWeight: 800, letterSpacing: '-.045em', color: 'var(--heading-ink)' }}>{info.nome_homenageado}</h1>
        <p style={{ margin: 0, color: 'var(--text-body)' }}>Uma prévia. Uma música. Pronta em até 30 minutos após o pagamento.</p>
      </header>
      <main style={{ maxWidth: 760, margin: '0 auto', padding: '18px 18px 80px' }}>
        {info.preview?.audio_url ? <PreviewPlayer url={info.preview.audio_url} name={info.nome_homenageado} /> : <div style={{ padding: 30, textAlign: 'center', color: 'var(--text-muted)' }}>Sua prévia ainda está sendo finalizada…</div>}
        {info.preview?.audio_url && <>
          <div style={{ display: 'flex', justifyContent: 'center', margin: '16px 0 34px' }}><a href={feedbackUrl} style={{ color: 'var(--text-muted)', fontSize: 13, textDecoration: 'underline' }}>Quero pedir uma alteração nesta prévia</a></div>
          <h2 style={{ margin: '0 0 16px', fontSize: 24, fontWeight: 800, letterSpacing: '-.03em', color: 'var(--heading-ink)' }}>Escolha como quer receber</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(270px,1fr))', gap: 16 }}>
            <PlanCard id="musica" selected={plan === 'musica'} onSelect={() => setPlan('musica')} price={info.valores.musica} />
            <PlanCard id="video" selected={plan === 'video'} onSelect={() => setPlan('video')} price={info.valores.video} />
          </div>
          {plan && <section style={{ marginTop: 26, padding: 24, borderRadius: 24, background: 'var(--surface-white)', border: '1.5px solid var(--border-soft)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 18, alignItems: 'baseline', marginBottom: 18 }}><strong style={{ color: 'var(--heading-ink)' }}>Total</strong><strong style={{ fontSize: 28, color: 'var(--heading-ink)' }}>R$ {finalPrice.toFixed(2).replace('.', ',')}</strong></div>
            <CouponBox slug={slug} plan={plan} applied={coupon} onApply={setCoupon} />
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 7, marginBottom: 20 }}>{(['pix','cartao','boleto'] as Tab[]).map((item) => <button key={item} type="button" onClick={() => setTab(item)} style={{ padding: 11, borderRadius: 99, border: tab === item ? '1.5px solid var(--gold)' : '1px solid var(--border-soft)', background: tab === item ? 'var(--gold-pale)' : 'transparent', color: tab === item ? 'var(--gold)' : 'var(--text-muted)', fontWeight: 700, cursor: 'pointer', textTransform: 'capitalize' }}>{item === 'cartao' ? 'Cartão' : item === 'boleto' ? 'Boleto' : 'PIX'}</button>)}</div>
            {tab === 'pix' && <PixTab slug={slug} plan={plan} valor={finalPrice} cupomCodigo={coupon?.codigo} />}
            {tab === 'cartao' && <CartaoTab slug={slug} plan={plan} valor={finalPrice} cupomCodigo={coupon?.codigo} onApproved={() => window.location.assign(`/ouvir-musica/${slug}/sucesso`)} />}
            {tab === 'boleto' && <BoletoTab slug={slug} plan={plan} cupomCodigo={coupon?.codigo} />}
          </section>}
          <p style={{ marginTop: 22, textAlign: 'center', color: 'var(--text-soft)', fontSize: 12, lineHeight: 1.6 }}>Pagamento seguro · Entrega em até 30 minutos · Se precisar, refazemos sem custo</p>
        </>}
      </main>
    </div>
  );
}
